import { useMemo } from 'react';

import { competencyTransform } from '@backrs_ui/common-ui';
import type { PriorityPlaylist } from '@backrs_ui/common-ui';

import extracurricularsBanner from '@backrs_ui/grow-ui/src/assets/images/extracurriculars-guide/extracurriculars-banner.png';
import mythVsRealitySticker from '@backrs_ui/grow-ui/src/assets/images/extracurriculars-guide/myth-vs-reality-sticker.png';
import {
  GuideMythQuiz,
  GuideNumberedTimeline,
  GuideNumberedTimelineItem,
  GuidePageShell,
  GuideSections,
  GuideStrategySelector,
  GuideToolsSection,
} from '@backrs_ui/grow-ui/src/components/GuidePage';
import { LubavButton } from '@backrs_ui/grow-ui/src/components/Lubav/LubavButton';
import { useAuth } from '@backrs_ui/grow-ui/src/hooks/useAuth';
import { StickerCardId } from '@backrs_ui/grow-ui/src/pages/Home/hooks/stickerCardMapping';
import { selectContentCompetencies } from '@backrs_ui/grow-ui/src/redux/contentCompetency/contentCompetency';
import { useGetContentNuggetsBySlugsQuery } from '@backrs_ui/grow-ui/src/redux/contentNugget/contentNugget.api';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { useGetSuggestedPlaylistBySlugQuery } from '@backrs_ui/grow-ui/src/redux/priorities/priorities.api';
import { openSuggestedPlaylistViewStep } from '@backrs_ui/grow-ui/src/redux/priorities/priorities.slice';

import { BeforeYouApplySection, CorePrinciplesSection } from './components';
import { useAgentContext } from './ExtracurricularsGuidePageContainer.agentContext';
import { extracurricularsContent } from '../content';

const SECTION_IDS = {
  overview: 'extracurriculars-section-overview',
  tools: 'extracurriculars-section-tools',
  corePrinciples: 'extracurriculars-section-core-principles',
  myths: 'extracurriculars-section-myths',
  fourYearFramework: 'extracurriculars-section-four-year-framework',
  strategy: 'extracurriculars-section-strategy',
  beforeYouApply: 'extracurriculars-section-before-you-apply',
} as const;

const EXTRACURRICULARS_TOOL_ID = 'extracurriculars_guide';

const EXTRACURRICULARS_TOOL_IDS: readonly StickerCardId[] = ['boost_your_profile'];

const EXTRACURRICULARS_PLAYBOOK_SLUG = 'boost-profile-hs';

const EXTRACURRICULARS_COMPETENCY_SLUG = 'academics-extracurriculars';

const EXTRACURRICULARS_NUGGET_SLUGS: readonly string[] = [
  '14-summer-activities-to-boost-your-college-application',
  'student-story-extracurriculars',
  'sample-then-specialize',
];

const TIMELINE_ITEMS: readonly GuideNumberedTimelineItem[] =
  extracurricularsContent.fourYearFramework.years.map(({ grade, ...year }) => ({
    ...year,
    badge: grade,
  }));

export const ExtracurricularsGuidePageContainer = () => {
  useAgentContext();

  const dispatch = useAppDispatch();
  const { loggedIn } = useAuth();

  const { data: resourceNuggets } = useGetContentNuggetsBySlugsQuery(EXTRACURRICULARS_NUGGET_SLUGS);
  const { contentCompetencies } = useAppSelector(selectContentCompetencies);

  const { data: playbook } = useGetSuggestedPlaylistBySlugQuery(EXTRACURRICULARS_PLAYBOOK_SLUG, {
    skip: !loggedIn,
  });

  const handlePlaybookClick = (playlist: PriorityPlaylist) => {
    dispatch(openSuggestedPlaylistViewStep(playlist));
  };

  const competencyNugget = useMemo(() => {
    const competency = contentCompetencies.find((c) => c.slug === EXTRACURRICULARS_COMPETENCY_SLUG);

    return competency ? competencyTransform(competency) : undefined;
  }, [contentCompetencies]);

  const orderedNuggets = useMemo(() => {
    if (!resourceNuggets) return [];

    const bySlug = new Map(resourceNuggets.map((n) => [n.slug, n]));

    return EXTRACURRICULARS_NUGGET_SLUGS.map((slug) => bySlug.get(slug)).filter(
      (n): n is NonNullable<typeof n> => Boolean(n),
    );
  }, [resourceNuggets]);

  const extraNuggets = useMemo(
    () => (competencyNugget ? [competencyNugget, ...orderedNuggets] : orderedNuggets),
    [competencyNugget, orderedNuggets],
  );

  const sections = [
    {
      id: SECTION_IDS.tools,
      title: extracurricularsContent.tools.title,
      subtitle: extracurricularsContent.tools.subtitle,
      rightSlot: (
        <LubavButton
          id="extracurriculars-tools-lubav-btn"
          label="What should I start with?"
          prompt="Which of the tools or resources in this section should I start with to strengthen my extracurricular profile?"
          allowTools={false}
        />
      ),
      content: (
        <GuideToolsSection toolIds={EXTRACURRICULARS_TOOL_IDS} extraNuggets={extraNuggets} />
      ),
    },
    {
      id: SECTION_IDS.corePrinciples,
      title: extracurricularsContent.corePrinciples.title,
      subtitle: extracurricularsContent.corePrinciples.subtitle,
      rightSlot: (
        <LubavButton
          id="extracurriculars-principles-lubav-btn"
          label="Which matters most for me?"
          prompt={`Based on what you know about me, which of the principles in the "${extracurricularsContent.corePrinciples.title}" section should I focus on most? Explain why and give me one actionable step.`}
          allowTools={false}
        />
      ),
      content: (
        <CorePrinciplesSection
          content={extracurricularsContent.corePrinciples}
          playbook={playbook}
          onPlaybookClick={handlePlaybookClick}
        />
      ),
    },
    {
      id: SECTION_IDS.myths,
      title: extracurricularsContent.mythsAndMistakes.title,
      subtitle: extracurricularsContent.mythsAndMistakes.subtitle,
      rightSlot: (
        <LubavButton
          id="extracurriculars-myths-lubav-btn"
          label="Discuss with Lubav"
          prompt={`I just read the "${extracurricularsContent.mythsAndMistakes.title}" section. Based on what you know about me, identify which myth or mistake I'm most at risk for and explain why. Then tell me what I should do differently.`}
          allowTools={false}
        />
      ),
      content: (
        <GuideMythQuiz
          idPrefix="extracurriculars-myth-quiz"
          content={extracurricularsContent.mythsAndMistakes}
          stickerSrc={mythVsRealitySticker}
          stickerAlt="Myth vs Reality"
        />
      ),
    },
    {
      id: SECTION_IDS.fourYearFramework,
      title: extracurricularsContent.fourYearFramework.title,
      subtitle: extracurricularsContent.fourYearFramework.subtitle,
      rightSlot: (
        <LubavButton
          id="extracurriculars-framework-lubav-btn"
          label="What should I do now?"
          prompt="Based on my grade level and current situation, what should I be doing right now with my extracurricular activities? Give me specific, actionable steps for this phase."
          allowTools={false}
        />
      ),
      content: <GuideNumberedTimeline items={TIMELINE_ITEMS} />,
    },
    {
      id: SECTION_IDS.strategy,
      title: extracurricularsContent.strategyByGoal.title,
      subtitle: extracurricularsContent.strategyByGoal.subtitle,
      rightSlot: (
        <LubavButton
          id="extracurriculars-strategy-lubav-btn"
          label="Which strategy fits me?"
          prompt={`Based on my profile, goals, and intended path, which of the game plans in the "${extracurricularsContent.strategyByGoal.title}" section fits me best? Give me personalized advice for my situation.`}
        />
      ),
      content: (
        <GuideStrategySelector
          idPrefix="extracurriculars-strategy"
          content={extracurricularsContent.strategyByGoal}
        />
      ),
    },
    {
      id: SECTION_IDS.beforeYouApply,
      title: extracurricularsContent.beforeYouApply.title,
      subtitle: extracurricularsContent.beforeYouApply.subtitle,
      rightSlot: (
        <LubavButton
          id="extracurriculars-before-you-apply-lubav-btn"
          label="Help me log an activity"
          prompt="Help me log one of my activities: my role, how long I've been involved and how many hours, what changed because I was there, and what I took away from it. Then tell me what story my activities list currently tells."
          allowTools={false}
        />
      ),
      content: <BeforeYouApplySection content={extracurricularsContent.beforeYouApply} />,
    },
  ];

  return (
    <GuidePageShell
      toolId={EXTRACURRICULARS_TOOL_ID}
      banner={{
        src: extracurricularsBanner,
        alt: 'High School Extracurricular Activities',
      }}
    >
      <GuideSections
        header={extracurricularsContent.header}
        overview={{
          id: SECTION_IDS.overview,
        }}
        sections={sections}
      />
    </GuidePageShell>
  );
};
